import { useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  Page,
  Navbar,
  Sheet,
  Block,
  List,
  ListItem,
  Toolbar,
  ToolbarPane,
  Link,
} from 'konsta/react'
import {
  Menu,
  X,
  LayoutDashboard,
  Users,
  Banknote,
  DoorOpen,
  UserCog,
  ArrowLeftRight,
  AlertTriangle,
  ShieldAlert,
  Lock,
  FileText,
  Activity,
  BarChart3,
  ClipboardList,
  Wrench,
  Settings,
  LogOut,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface NavItem {
  label: string
  path: string
  icon: React.ElementType
}

const navItems: NavItem[] = [
  { label: '儀表板', path: '/admin', icon: LayoutDashboard },
  { label: '用戶管理', path: '/admin/users', icon: Users },
  { label: '提款管理', path: '/admin/withdrawals', icon: Banknote },
  { label: '房間管理', path: '/admin/rooms', icon: DoorOpen },
  { label: '代理管理', path: '/admin/agents', icon: UserCog },
  { label: '交易記錄', path: '/admin/transactions', icon: ArrowLeftRight },
  { label: '爭議處理', path: '/admin/disputes', icon: AlertTriangle },
  { label: '反欺詐', path: '/admin/fraud', icon: ShieldAlert },
  { label: '凍結底池', path: '/admin/frozen-pots', icon: Lock },
  { label: '手牌記錄', path: '/admin/hands', icon: FileText },
  { label: '系統健康', path: '/admin/health', icon: Activity },
  { label: '報表', path: '/admin/reports', icon: BarChart3 },
  { label: '審計日誌', path: '/admin/audit', icon: ClipboardList },
  { label: '系統維護', path: '/admin/maintenance', icon: Wrench },
  { label: '系統設定', path: '/admin/config', icon: Settings },
]

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin'
    }
    return location.pathname.startsWith(path)
  }

  const handleNav = (path: string) => {
    navigate(path)
    setSidebarOpen(false)
  }

  return (
    <Page>
      <Navbar
        title="管理後台"
        left={
          <Link onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </Link>
        }
      />

      <Outlet />

      <Sheet
        className="pb-safe"
        opened={sidebarOpen}
        onBackdropClick={() => setSidebarOpen(false)}
        style={{ height: 'auto', maxHeight: '85vh' }}
      >
        <Toolbar top className="justify-between pt-4">
          <ToolbarPane>
            <span className="font-semibold text-base pl-2">管理選單</span>
          </ToolbarPane>
          <ToolbarPane>
            <Link iconOnly onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Link>
          </ToolbarPane>
        </Toolbar>

        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 120px)' }}>
          <List strongIos insetIos className="!my-0">
            {navItems.map((item) => {
              const Icon = item.icon
              const active = isActive(item.path)
              return (
                <ListItem
                  key={item.path}
                  link
                  chevronIos
                  onClick={() => handleNav(item.path)}
                  title={
                    <span
                      className={cn(
                        'font-medium',
                        active ? 'text-[#007AFF]' : 'text-black'
                      )}
                    >
                      {item.label}
                    </span>
                  }
                  media={
                    <Icon
                      className={cn(
                        'w-5 h-5',
                        active ? 'text-[#007AFF]' : 'text-[#8E8E93]'
                      )}
                    />
                  }
                />
              )
            })}
          </List>

          <Block className="!mt-2 !mb-4">
            <button
              onClick={() => handleNav('/')}
              className="flex items-center gap-2 text-sm font-medium text-[#FF3B30] w-full px-2 py-2"
            >
              <LogOut className="w-4 h-4" />
              返回前台
            </button>
          </Block>
        </div>
      </Sheet>
    </Page>
  )
}
